import { useState, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Search, RotateCcw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CryptoWord {
  id: string;
  length: number;
  numbers: number[];
  results: string[];
  isLoading: boolean;
}

// Generate a consistent color for a number (1-26)
const getNumberColor = (num: number): string => {
  const colors = [
    "bg-red-100 dark:bg-red-900/30",
    "bg-blue-100 dark:bg-blue-900/30",
    "bg-green-100 dark:bg-green-900/30",
    "bg-yellow-100 dark:bg-yellow-900/30",
    "bg-purple-100 dark:bg-purple-900/30",
    "bg-pink-100 dark:bg-pink-900/30",
    "bg-indigo-100 dark:bg-indigo-900/30",
    "bg-orange-100 dark:bg-orange-900/30",
    "bg-teal-100 dark:bg-teal-900/30",
    "bg-cyan-100 dark:bg-cyan-900/30",
    "bg-lime-100 dark:bg-lime-900/30",
    "bg-amber-100 dark:bg-amber-900/30",
    "bg-emerald-100 dark:bg-emerald-900/30",
    "bg-violet-100 dark:bg-violet-900/30",
    "bg-fuchsia-100 dark:bg-fuchsia-900/30",
    "bg-rose-100 dark:bg-rose-900/30",
    "bg-sky-100 dark:bg-sky-900/30",
    "bg-slate-100 dark:bg-slate-900/30",
    "bg-zinc-100 dark:bg-zinc-900/30",
    "bg-stone-100 dark:bg-stone-900/30",
    "bg-red-200 dark:bg-red-800/30",
    "bg-blue-200 dark:bg-blue-800/30",
    "bg-green-200 dark:bg-green-800/30",
    "bg-yellow-200 dark:bg-yellow-800/30",
    "bg-purple-200 dark:bg-purple-800/30",
    "bg-pink-200 dark:bg-pink-800/30",
  ];
  return colors[(num - 1) % colors.length];
};

export const CryptoSolver = () => {
  const [words, setWords] = useState<CryptoWord[]>([
    { id: "1", length: 5, numbers: Array(5).fill(0), results: [], isLoading: false },
  ]);
  const [legend, setLegend] = useState<Record<number, string>>({});
  const [isCalculating, setIsCalculating] = useState(false);

  // Get all unique numbers used across all words
  const usedNumbers = useMemo(() => {
    const nums = new Set<number>();
    words.forEach(word => {
      word.numbers.forEach(n => {
        if (n > 0) nums.add(n);
      });
    });
    return Array.from(nums).sort((a, b) => a - b);
  }, [words]);

  const addWord = useCallback(() => {
    const newId = String(Date.now());
    setWords(prev => [
      ...prev,
      { id: newId, length: 5, numbers: Array(5).fill(0), results: [], isLoading: false },
    ]);
  }, []);

  const removeWord = useCallback((id: string) => {
    setWords(prev => prev.filter(w => w.id !== id));
  }, []);

  const updateWordLength = useCallback((id: string, length: number) => {
    setWords(prev =>
      prev.map(w => {
        if (w.id !== id) return w;
        const newNumbers = Array(length).fill(0);
        // Preserve existing numbers where possible
        for (let i = 0; i < Math.min(w.numbers.length, length); i++) {
          newNumbers[i] = w.numbers[i];
        }
        return { ...w, length, numbers: newNumbers, results: [] };
      })
    );
  }, []);

  const updateWordNumber = useCallback((wordId: string, position: number, num: number) => {
    setWords(prev =>
      prev.map(w => {
        if (w.id !== wordId) return w;
        const newNumbers = [...w.numbers];
        newNumbers[position] = num;
        return { ...w, numbers: newNumbers, results: [] };
      })
    );
  }, []);

  const updateLegendLetter = useCallback((num: number, letter: string) => {
    // Normalize to NFC and get first character (important for Swedish chars like Ö)
    const normalizedLetter = letter.normalize('NFC').toUpperCase().slice(0, 1);
    // Allow A-Z and Swedish characters (using Unicode ranges)
    if (normalizedLetter && !/^[A-ZÅÄÖ]$/u.test(normalizedLetter)) return;
    
    setLegend(prev => {
      if (!normalizedLetter) {
        const newLegend = { ...prev };
        delete newLegend[num];
        return newLegend;
      }
      return { ...prev, [num]: normalizedLetter };
    });
    // Clear results when legend changes
    setWords(prev => prev.map(w => ({ ...w, results: [] })));
  }, []);

  const resetAll = useCallback(() => {
    setWords([{ id: "1", length: 5, numbers: Array(5).fill(0), results: [], isLoading: false }]);
    setLegend({});
  }, []);

  // Filter words based on pattern and legend constraints
  const filterMatchingWords = useCallback(
    (dbWords: string[], numbers: number[], currentLegend: Record<number, string>): string[] => {
      // Build reverse mapping: letter -> number (from legend)
      const letterToNum: Record<string, number> = {};
      for (const [numStr, letter] of Object.entries(currentLegend)) {
        const normalizedLetter = letter.normalize('NFC').toUpperCase();
        letterToNum[normalizedLetter] = parseInt(numStr);
      }
      
      return dbWords.filter(word => {
        // Normalize to NFC for consistent Unicode comparison (important for Ö, Ä, Å)
        const upperWord = word.normalize('NFC').toUpperCase();
        
        // Check if known letters from legend match
        for (let i = 0; i < numbers.length; i++) {
          const num = numbers[i];
          if (num > 0 && currentLegend[num]) {
            // Normalize both sides for comparison
            const legendLetter = currentLegend[num].normalize('NFC');
            const wordLetter = upperWord[i];
            
            // Use localeCompare for Swedish characters - returns 0 if equal
            if (!wordLetter || wordLetter.localeCompare(legendLetter, 'sv', { sensitivity: 'base' }) !== 0) {
              return false;
            }
          }
        }
        
        // Check constraints: same number = same letter, different number = different letter
        const numToLetter: Record<number, string> = {};
        const letterToNumLocal: Record<string, number> = { ...letterToNum };
        
        for (let i = 0; i < numbers.length; i++) {
          const num = numbers[i];
          if (num > 0) {
            const wordLetter = upperWord[i];
            
            if (numToLetter[num]) {
              // Same number must map to same letter
              if (numToLetter[num].localeCompare(wordLetter, 'sv', { sensitivity: 'base' }) !== 0) {
                return false;
              }
            } else {
              // Check if this letter is already used by a DIFFERENT number
              if (letterToNumLocal[wordLetter] && letterToNumLocal[wordLetter] !== num) {
                return false; // Different number already uses this letter
              }
              numToLetter[num] = wordLetter;
              letterToNumLocal[wordLetter] = num;
            }
          }
        }
        
        return true;
      });
    },
    []
  );

  // Cross-word constraint: check if a word's letter mapping is consistent with all other words
  // This enforces both:
  // 1. Same number = same letter across all words
  // 2. Different numbers = different letters (unique mapping)
  const isConsistentWithOtherWords = useCallback(
    (
      candidateWord: string,
      candidateNumbers: number[],
      otherWordsWithResults: { numbers: number[]; results: string[] }[],
      currentLegend: Record<number, string>
    ): boolean => {
      // Normalize candidate word for consistent comparison
      const normalizedCandidate = candidateWord.normalize('NFC').toUpperCase();
      
      // Build the mapping from this candidate word
      const numToLetter: Record<number, string> = {};
      const letterToNum: Record<string, number> = {};
      
      // Start with legend mappings
      for (const [numStr, letter] of Object.entries(currentLegend)) {
        const normalizedLetter = letter.normalize('NFC').toUpperCase();
        const num = parseInt(numStr);
        numToLetter[num] = normalizedLetter;
        letterToNum[normalizedLetter] = num;
      }
      
      // Add mappings from candidate word
      for (let i = 0; i < candidateNumbers.length; i++) {
        const num = candidateNumbers[i];
        if (num > 0) {
          const letter = normalizedCandidate[i];
          
          // Check consistency with existing mappings
          if (numToLetter[num] && numToLetter[num] !== letter) {
            return false; // Same number must have same letter
          }
          if (letterToNum[letter] && letterToNum[letter] !== num) {
            return false; // Same letter must have same number
          }
          
          numToLetter[num] = letter;
          letterToNum[letter] = num;
        }
      }
      
      // Check if at least one word from each other row is compatible
      for (const other of otherWordsWithResults) {
        if (other.results.length === 0) continue;
        
        const hasCompatibleWord = other.results.some(otherWord => {
          const normalizedOther = otherWord.normalize('NFC').toUpperCase();
          
          // Create a copy of mappings for this check
          const localNumToLetter: Record<number, string> = { ...numToLetter };
          const localLetterToNum: Record<string, number> = { ...letterToNum };
          
          for (let i = 0; i < other.numbers.length; i++) {
            const num = other.numbers[i];
            if (num > 0) {
              const letter = normalizedOther[i];
              
              // Check same number = same letter
              if (localNumToLetter[num] && localNumToLetter[num] !== letter) {
                return false;
              }
              // Check same letter = same number (unique mapping)
              if (localLetterToNum[letter] && localLetterToNum[letter] !== num) {
                return false;
              }
              
              localNumToLetter[num] = letter;
              localLetterToNum[letter] = num;
            }
          }
          return true;
        });
        
        if (!hasCompatibleWord) {
          return false;
        }
      }
      
      return true;
    },
    []
  );

  const calculateSolutions = useCallback(async () => {
    // Check if any word has numbers set
    const hasNumbers = words.some(w => w.numbers.some(n => n > 0));
    if (!hasNumbers) {
      toast.error("Ange siffror för minst ett ord först");
      return;
    }

    setIsCalculating(true);
    
    try {
      // Build a LIKE pattern from known letters in legend
      const buildLikePattern = (numbers: number[], length: number): string => {
        let pattern = '';
        for (let i = 0; i < length; i++) {
          const num = numbers[i] || 0;
          if (num > 0 && legend[num]) {
            pattern += legend[num].normalize('NFC');
          } else {
            pattern += '_';
          }
        }
        return pattern;
      };
      
      // First pass: fetch matching words for each row using server-side filtering
      const wordsWithResults = await Promise.all(
        words.map(async (word, wordIndex) => {
          if (!word.numbers.some(n => n > 0)) {
            return { ...word, results: [] };
          }
          
          // Build LIKE pattern for server-side filtering
          const likePattern = buildLikePattern(word.numbers, word.length);
          
          // Fetch words using LIKE pattern for efficient server-side filtering
          let query = supabase
            .from("words")
            .select("word_upper")
            .eq("length", word.length)
            .eq("language", 1);
          
          // Only use LIKE if we have at least one known letter
          const hasKnownLetters = likePattern.replace(/_/g, '').length > 0;
          if (hasKnownLetters) {
            query = query.like("word_upper", likePattern);
          }
          
          // Fetch all matching words - we need complete data for accurate cross-filtering
          // Use pagination to get all results since Supabase has a 1000 row default limit
          const allWords: string[] = [];
          let offset = 0;
          const pageSize = 1000;
          
          while (true) {
            const { data, error } = await query.range(offset, offset + pageSize - 1);
            
            if (error) throw error;
            
            if (!data || data.length === 0) break;
            
            allWords.push(...data.map(d => d.word_upper));
            
            // If we got less than pageSize, we've reached the end
            if (data.length < pageSize) break;
            
            offset += pageSize;
            
            // Safety limit to prevent infinite loops (max 100k words)
            if (offset >= 100000) break;
          }
          
          const filtered = filterMatchingWords(allWords, word.numbers, legend);
          
          return { ...word, results: filtered };
        })
      );

      // Second pass: cross-filter based on shared numbers
      const finalResults = wordsWithResults.map((word, idx) => {
        if (word.results.length === 0) return word;
        
        const otherWords = wordsWithResults
          .filter((_, i) => i !== idx)
          .map(w => ({ numbers: w.numbers, results: w.results }));
        
        // Only do cross-filtering if there are other words with results
        const hasOtherResults = otherWords.some(o => o.results.length > 0);
        if (!hasOtherResults) return word;
        
        const crossFiltered = word.results.filter(candidate =>
          isConsistentWithOtherWords(candidate, word.numbers, otherWords, legend)
        );
        
        return { ...word, results: crossFiltered.slice(0, 100) };
      });
      
      setWords(finalResults.map(w => ({ ...w, isLoading: false })));
      
      // Update legend with definite letters (where all results agree)
      const newLegend = { ...legend };
      finalResults.forEach(word => {
        if (word.results.length > 0) {
          for (let i = 0; i < word.numbers.length; i++) {
            const num = word.numbers[i];
            if (num > 0 && !newLegend[num]) {
              const letters = new Set(word.results.map(r => r[i]));
              if (letters.size === 1) {
                newLegend[num] = Array.from(letters)[0];
              }
            }
          }
        }
      });
      setLegend(newLegend);
      
      const totalResults = finalResults.reduce((sum, w) => sum + w.results.length, 0);
      if (totalResults === 0) {
        toast.info("Inga matchande ord hittades med dessa begränsningar");
      } else {
        toast.success(`Hittade ${totalResults} möjliga ord`);
      }
    } catch (error) {
      console.error("Error calculating solutions:", error);
      toast.error("Ett fel uppstod vid beräkning");
    } finally {
      setIsCalculating(false);
    }
  }, [words, legend, filterMatchingWords, isConsistentWithOtherWords]);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-lg font-semibold text-foreground mb-1">Kryptokryss-lösare</h2>
        <p className="text-sm text-muted-foreground">
          Ange siffror för varje position i orden. Samma siffra = samma bokstav.
        </p>
      </div>

      {/* Word rows */}
      <div className="space-y-4">
        {words.map((word, wordIndex) => (
          <Card key={word.id} className="relative">
            <CardContent className="pt-4 pb-4">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-medium text-muted-foreground w-12">
                  Ord {wordIndex + 1}:
                </span>
                
                <Select
                  value={String(word.length)}
                  onValueChange={(v) => updateWordLength(word.id, parseInt(v))}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 15 }, (_, i) => i + 2).map(len => (
                      <SelectItem key={len} value={String(len)}>
                        {len}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="flex gap-1 flex-wrap">
                  {word.numbers.map((num, pos) => (
                    <div key={pos} className="relative">
                      <Input
                        type="number"
                        min={0}
                        max={26}
                        value={num || ""}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0;
                          if (val >= 0 && val <= 26) {
                            updateWordNumber(word.id, pos, val);
                          }
                        }}
                        className={`w-12 h-10 text-center p-1 ${
                          num > 0 ? getNumberColor(num) : ""
                        }`}
                        placeholder="?"
                      />
                      {num > 0 && legend[num] && (
                        <div className="absolute -bottom-4 left-0 right-0 text-center text-xs font-bold text-primary">
                          {legend[num]}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {words.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeWord(word.id)}
                    className="ml-auto"
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>

              {/* Results for this word */}
              {word.results.length > 0 && (
                <div className="mt-4 pt-3 border-t">
                  <p className="text-sm text-muted-foreground mb-2">
                    Möjliga ord ({word.results.length}):
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {word.results.slice(0, 20).map((result, i) => (
                      <span
                        key={i}
                        className="px-2 py-1 bg-muted rounded text-sm font-mono"
                      >
                        {result}
                      </span>
                    ))}
                    {word.results.length > 20 && (
                      <span className="px-2 py-1 text-sm text-muted-foreground">
                        +{word.results.length - 20} till...
                      </span>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Button variant="outline" onClick={addWord} className="w-full">
        <Plus className="h-4 w-4 mr-2" />
        Lägg till ord
      </Button>

      {/* Legend */}
      {usedNumbers.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Siffra → Bokstav</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-6 sm:grid-cols-9 md:grid-cols-13 gap-2">
              {usedNumbers.map(num => (
                <div key={num} className="text-center">
                  <div
                    className={`w-10 h-8 flex items-center justify-center rounded-t text-sm font-medium ${getNumberColor(num)}`}
                  >
                    {num}
                  </div>
                  <Input
                    value={legend[num] || ""}
                    onChange={(e) => updateLegendLetter(num, e.target.value)}
                    className="w-10 h-8 text-center p-0 rounded-t-none text-sm font-bold uppercase"
                    maxLength={1}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action buttons */}
      <div className="flex gap-3">
        <Button onClick={calculateSolutions} disabled={isCalculating} className="flex-1">
          <Search className="h-4 w-4 mr-2" />
          {isCalculating ? "Beräknar..." : "Beräkna möjliga lösningar"}
        </Button>
        <Button variant="outline" onClick={resetAll}>
          <RotateCcw className="h-4 w-4 mr-2" />
          Återställ
        </Button>
      </div>
    </div>
  );
};
